/**
 * Email signup popup — Broadcast theme
 * -----------------------------------------------------------------
 * Shows an accessible modal after the visitor either scrolls for N
 * seconds or spends N seconds on the site (whichever happens first).
 * Skips entirely if the visitor already dismissed it (1 day cookie,
 * configurable) or already subscribed (permanent cookie).
 *
 * After a successful signup Shopify reloads the page. We detect that
 * via a marker rendered inside the {% form %} block
 * (form.posted_successfully?) or the ?customer_posted=true return
 * parameter, then re-open the popup showing the success message. It
 * stays open until the visitor clicks X, clicks outside, or presses
 * Escape.
 */
(function () {
  'use strict';

  var DISMISS_COOKIE = 'email_popup_dismissed';
  var SUBSCRIBE_COOKIE = 'email_popup_subscribed';
  var SUBSCRIBE_COOKIE_DAYS = 365;

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(init);

  function justSubscribed(popup) {
    if (popup.querySelector('[data-email-popup-posted]')) return true;
    return /[?&]customer_posted=true(&|$)/.test(window.location.search);
  }

  function cleanUrl() {
    if (!window.history || !window.history.replaceState) return;
    var url = window.location.pathname +
      window.location.search
        .replace(/([?&])customer_posted=true(&|$)/, function (m, a, b) { return b ? a : ''; })
        .replace(/[?&]$/, '') +
      window.location.hash;
    window.history.replaceState({}, document.title, url);
  }

  function init() {
    var popup = document.querySelector('[data-email-popup]');
    if (!popup) return;

    // Disabled inside the Shopify theme editor (see "Show popup in theme editor").
    if (popup.dataset.disabled === 'true') return;

    if (justSubscribed(popup)) {
      setCookie(SUBSCRIBE_COOKIE, '1', SUBSCRIBE_COOKIE_DAYS);
      cleanUrl();
      openPopup(popup);
      return;
    }

    if (getCookie(SUBSCRIBE_COOKIE) || getCookie(DISMISS_COOKIE)) {
      return;
    }

    var scrollDelay = (parseFloat(popup.dataset.scrollDelay) || 10) * 1000;
    var timeDelay = (parseFloat(popup.dataset.timeDelay) || 30) * 1000;

    var triggered = false;
    var scrollTimer = null;
    var timeTimer = null;

    function trigger() {
      if (triggered) return;
      triggered = true;
      clearTimeout(scrollTimer);
      clearTimeout(timeTimer);
      window.removeEventListener('scroll', onScroll);
      openPopup(popup);
    }

    function onScroll() {
      if (scrollTimer) return;
      window.removeEventListener('scroll', onScroll);
      scrollTimer = setTimeout(trigger, scrollDelay);
    }

    timeTimer = setTimeout(trigger, timeDelay);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function wireDismissControls(popup) {
    var closeButtons = popup.querySelectorAll('[data-email-popup-close]');
    var overlay = popup.querySelector('[data-email-popup-overlay]');
    var dismissDays = parseFloat(popup.dataset.dismissDays) || 1;

    function dismiss() {
      setCookie(DISMISS_COOKIE, '1', dismissDays);
      closePopup(popup);
    }

    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', dismiss);
    });

    if (overlay) {
      overlay.addEventListener('click', dismiss);
    }

    popup._emailPopupDismiss = dismiss;
  }

  var lastFocusedElement = null;

  function openPopup(popup) {
    if (!popup._emailPopupDismiss) {
      wireDismissControls(popup);
    }

    lastFocusedElement = document.activeElement;

    popup.hidden = false;
    document.body.style.overflow = 'hidden';

    var mainContent = document.querySelector('#MainContent, main');
    if (mainContent && 'inert' in mainContent) {
      mainContent.inert = true;
    } else if (mainContent) {
      mainContent.setAttribute('aria-hidden', 'true');
    }

    var dialog = popup.querySelector('[data-email-popup-dialog]');
    var heading = popup.querySelector('#email-popup-heading');
    var blurb = popup.querySelector('#email-popup-blurb');
    var success = popup.querySelector('[data-email-popup-success]');
    var announcer = document.querySelector('[data-email-popup-announcer]');

    // Speak the offer (or the success message) via a live region so it's
    // heard on open regardless of how the visitor navigates afterward.
    if (announcer) {
      var parts = [];
      if (heading) parts.push(heading.textContent.replace(/\s+/g, ' ').trim());
      if (blurb) parts.push(blurb.textContent.trim());
      if (success) parts.push(success.textContent.trim());
      announcer.textContent = parts.join('. ');
    }

    window.requestAnimationFrame(function () {
      if (dialog) {
        dialog.focus();
      } else if (heading) {
        heading.focus();
      }
    });

    document.addEventListener('keydown', onKeydown);

    popup._focusTrapCleanup = trapFocus(dialog);
  }

  function closePopup(popup) {
    popup.hidden = true;
    document.body.style.overflow = '';

    var mainContent = document.querySelector('#MainContent, main');
    if (mainContent && 'inert' in mainContent) {
      mainContent.inert = false;
    } else if (mainContent) {
      mainContent.removeAttribute('aria-hidden');
    }

    document.removeEventListener('keydown', onKeydown);

    if (typeof popup._focusTrapCleanup === 'function') {
      popup._focusTrapCleanup();
    }

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var popup = document.querySelector('[data-email-popup]:not([hidden])');
    if (popup && typeof popup._emailPopupDismiss === 'function') {
      popup._emailPopupDismiss();
    }
  }

  function trapFocus(container) {
    if (!container) return function () {};

    var focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function handleTab(event) {
      if (event.key !== 'Tab') return;

      var focusable = Array.prototype.slice.call(container.querySelectorAll(focusableSelector));
      focusable = focusable.filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handleTab);
    return function cleanup() {
      container.removeEventListener('keydown', handleTab);
    };
  }
})();